import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { MarkdownBody } from "@/components/blog/MarkdownBody";
import { getArticleBySlug } from "@/lib/blog.functions";
import { Calendar, Clock, User, ShieldCheck, ArrowUpRight, HelpCircle, Sparkles, BookOpen } from "lucide-react";

const PALETTES = [
  { grad: "from-primary/20 via-primary/5 to-transparent", bar: "bg-primary", chip: "bg-primary/10 text-primary", ring: "ring-primary/20", accent: "text-primary" },
  { grad: "from-emerald-500/20 via-emerald-500/5 to-transparent", bar: "bg-emerald-500", chip: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300", ring: "ring-emerald-500/20", accent: "text-emerald-600 dark:text-emerald-400" },
  { grad: "from-amber-500/20 via-amber-500/5 to-transparent", bar: "bg-amber-500", chip: "bg-amber-500/10 text-amber-700 dark:text-amber-300", ring: "ring-amber-500/20", accent: "text-amber-600 dark:text-amber-400" },
  { grad: "from-rose-500/20 via-rose-500/5 to-transparent", bar: "bg-rose-500", chip: "bg-rose-500/10 text-rose-700 dark:text-rose-300", ring: "ring-rose-500/20", accent: "text-rose-600 dark:text-rose-400" },
  { grad: "from-sky-500/20 via-sky-500/5 to-transparent", bar: "bg-sky-500", chip: "bg-sky-500/10 text-sky-700 dark:text-sky-300", ring: "ring-sky-500/20", accent: "text-sky-600 dark:text-sky-400" },
  { grad: "from-violet-500/20 via-violet-500/5 to-transparent", bar: "bg-violet-500", chip: "bg-violet-500/10 text-violet-700 dark:text-violet-300", ring: "ring-violet-500/20", accent: "text-violet-600 dark:text-violet-400" },
];

function hashIndex(key: string) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return h % PALETTES.length;
}

function readingTime(text?: string | null) {
  if (!text) return 3;
  const words = text.split(/\s+/).length;
  return Math.max(2, Math.round(words / 220));
}

const SITE = "https://heallexa.com";
const DEFAULT_OG_IMAGE = `${SITE}/og-image.jpg`;
const LOGO_URL = `${SITE}/logo.png`;

export const Route = createFileRoute("/blog/article/$slug")({
  loader: async ({ params }) => {
    await setSeoPageCache();
    const res = await getArticleBySlug({ data: { slug: params.slug } });
    if (!res) throw notFound();
    return res;
  },
  head: ({ loaderData }) => {
    const a = loaderData?.article;
    if (!a) return { meta: [] };
    const title = a.meta_title || a.title;
    const desc = a.meta_description || a.excerpt || "Healthcare discovery guide from Heallexa.";
    const canonical = a.canonical_url || `${SITE}/blog/article/${a.slug}`;
    const ogImage = (a as any).og_image || DEFAULT_OG_IMAGE;

    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: a.title,
      datePublished: a.published_at,
      dateModified: a.last_updated_at,
      image: ogImage,
      author: {
        "@type": "Organization",
        name: a.author_name || "Heallexa Editorial Team",
      },
      publisher: {
        "@type": "Organization",
        name: "Heallexa",
        url: SITE,
        logo: { "@type": "ImageObject", url: LOGO_URL },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      url: canonical,
      description: desc,
      isPartOf: { "@id": `${SITE}/blog` },
    };

    const faqSchema =
      Array.isArray(a.faq_json) && a.faq_json.length > 0
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: (a.faq_json as any[]).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }
        : null;

    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { property: "article:published_time", content: a.published_at ?? "" },
        { property: "article:modified_time", content: a.last_updated_at ?? "" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(articleSchema) },
        ...(faqSchema
          ? [{ type: "application/ld+json", children: JSON.stringify(faqSchema) }]
          : []),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-32 mx-auto max-w-3xl px-6 text-center">
        <h1 className="text-2xl font-semibold">Article not found</h1>
        <Link to="/blog" className="text-primary underline mt-4 inline-block">Back to blog</Link>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-32 mx-auto max-w-3xl px-6">
        <p className="text-destructive">{error.message}</p>
      </div>
    </div>
  ),
  component: BlogArticlePage,
});

function BlogArticlePage() {
  const { article, category } = Route.useLoaderData();
  const a = article as any;
  const internalLinks = (a.internal_links_json ?? []) as Array<{ url: string; label: string }>;
  const faq = (a.faq_json ?? []) as Array<{ q: string; a: string }>;
  const palette = PALETTES[hashIndex(a.category_id ?? a.id ?? a.slug)];
  const minutes = readingTime(a.content || a.excerpt);
  const publishedDate = a.published_at
    ? new Date(a.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : null;
  const isEditorsPick = typeof a.seo_score === "number" && a.seo_score >= 80;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <article className="pt-24 pb-16">
        {/* Hero */}
        <header className={`relative overflow-hidden border-b border-border bg-gradient-to-br ${palette.grad}`}>
          <div className={`absolute inset-x-0 top-0 h-1 ${palette.bar}`} />
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gradient-to-br from-white/40 to-transparent blur-3xl pointer-events-none" />
          <div className="relative mx-auto max-w-3xl px-6 pt-12 pb-12">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider">
              <Link to="/blog" className="text-muted-foreground hover:text-foreground transition-colors">Blog</Link>
              {category && (
                <>
                  <span className="text-muted-foreground" aria-hidden="true">/</span>
                  <Link
                    to="/blog/$category"
                    params={{ category: category.slug }}
                    className={`inline-flex items-center px-2.5 py-1 rounded-full font-medium ${palette.chip}`}
                  >
                    {category.name}
                  </Link>
                </>
              )}
              {isEditorsPick && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 font-medium">
                  <Sparkles className="w-3 h-3" /> Editor's pick
                </span>
              )}
            </div>
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight mt-4 leading-tight">
              {a.h1 || a.title}
            </h1>
            {a.excerpt && <p className="text-muted-foreground mt-4 text-lg leading-relaxed">{a.excerpt}</p>}

            {/* Meta strip */}
            <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-muted-foreground">
              {a.author_name && (
                <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" /> {a.author_name}</span>
              )}
              {a.reviewer_name && (
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className={`w-4 h-4 ${palette.accent}`} /> Reviewed by {a.reviewer_name}</span>
              )}
              {publishedDate && (
                <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {publishedDate}</span>
              )}
              <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {minutes} min read</span>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-3xl px-6 py-10">
          {/* TL;DR card */}
          {a.excerpt && (
            <div className={`relative rounded-2xl border border-border bg-card p-6 mb-10 ring-1 ${palette.ring}`}>
              <div className={`absolute left-0 top-6 bottom-6 w-1 rounded-r-full ${palette.bar}`} />
              <div className="flex items-start gap-3 pl-3">
                <BookOpen className={`w-5 h-5 mt-0.5 ${palette.accent}`} />
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">In short</p>
                  <p className="mt-1 text-foreground leading-relaxed">{a.excerpt}</p>
                </div>
              </div>
            </div>
          )}

          {a.content ? <MarkdownBody source={a.content} /> : <p className="text-muted-foreground">Content coming soon.</p>}

          {faq.length > 0 && (
            <section className="mt-14">
              <div className="flex items-center gap-2 mb-6">
                <HelpCircle className={`w-5 h-5 ${palette.accent}`} />
                <h2 className="text-2xl font-semibold">Frequently asked questions</h2>
              </div>
              <div className="space-y-3">
                {faq.map((f, i) => {
                  const p = PALETTES[(hashIndex(a.id ?? a.slug) + i) % PALETTES.length];
                  return (
                    <div key={i} className="group relative overflow-hidden rounded-xl border border-border bg-card p-5 transition-all hover:shadow-md hover:-translate-y-0.5">
                      <div className={`absolute left-0 top-0 bottom-0 w-1 ${p.bar}`} />
                      <div className="flex items-start gap-4 pl-2">
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${p.chip}`}>
                          {i + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-foreground">{f.q}</p>
                          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.a}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {internalLinks.length > 0 && (
            <section className="mt-14">
              <div className="flex items-center gap-2 mb-6">
                <ArrowUpRight className={`w-5 h-5 ${palette.accent}`} />
                <h2 className="text-2xl font-semibold">Explore on Heallexa</h2>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {internalLinks.map((l, i) => {
                  const p = PALETTES[(hashIndex(l.url) + i) % PALETTES.length];
                  return (
                    <li key={i}>
                      <a
                        href={l.url}
                        className={`group relative flex items-center justify-between gap-3 rounded-xl border border-border bg-gradient-to-br ${p.grad} p-4 transition-all hover:shadow-md hover:-translate-y-0.5 hover:border-foreground/20`}
                      >
                        <span className="font-medium text-foreground text-sm">{l.label}</span>
                        <ArrowUpRight className={`w-4 h-4 ${p.accent} transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5`} />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {a.medical_disclaimer && (
            <div className="mt-14 rounded-xl border border-border bg-muted/40 p-5">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <p className="text-xs text-muted-foreground leading-relaxed">{a.medical_disclaimer}</p>
              </div>
            </div>
          )}
        </div>
      </article>
      <FooterSection />
    </div>
  );
}
